import { Bell, CheckCircle, Loader2, Smartphone } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import type { PushConsent } from '@/lib/auth-types';
import { isIosSafariNonStandalone, supportsPushNotifications } from '@/lib/platform';
import { useAuthStore } from '@/stores/authStore';
import { STRINGS } from '@/strings/strings';

export function PushConsentRoute() {
  const navigate = useNavigate();
  const updateProfile = useAuthStore((s) => s.updateProfile);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isIosNonStandalone = isIosSafariNonStandalone();
  const S = STRINGS.onboarding.pushConsent;

  const goForward = () => navigate('/onboarding/world', { replace: true });

  const handlePrimaryCta = async () => {
    if (isIosNonStandalone) {
      goForward();
      return;
    }

    if (!supportsPushNotifications()) {
      goForward();
      return;
    }

    setIsSubmitting(true);

    let permission: NotificationPermission;
    try {
      permission = await Notification.requestPermission();
    } catch {
      permission = 'default';
    }

    if (permission === 'default') {
      goForward();
      return;
    }

    const consent: PushConsent = permission === 'granted' ? 'asked_yes' : 'asked_no';

    try {
      await updateProfile({ pushConsent: consent });
    } catch (err) {
      console.error('[PushConsent] failed to persist pushConsent:', err);
    }

    goForward();
  };

  const valueProps = [S.valueProp1, S.valueProp2, S.valueProp3];

  return (
    <div className="text-center">
      <Bell className="w-20 h-20 mx-auto text-dream-gold mb-6 animate-pulse" strokeWidth={1.5} />

      <h1 className="text-[22px] font-semibold text-dream-white mb-6">{S.title}</h1>

      <ul className="text-left space-y-3 mb-6">
        {valueProps.map((text) => (
          <li key={text} className="flex items-start gap-3 text-sm text-dream-white/90">
            <CheckCircle className="w-5 h-5 shrink-0 mt-0.5 text-dream-gold" strokeWidth={2} />
            <span className="leading-relaxed">{text}</span>
          </li>
        ))}
      </ul>

      {isIosNonStandalone && (
        <div className="mb-6 flex gap-3 items-start p-3 rounded-lg bg-white/5 border border-white/10 text-left">
          <Smartphone className="w-6 h-6 shrink-0 text-dream-gold mt-0.5" strokeWidth={1.8} />
          <div className="text-xs text-dream-white/90 leading-relaxed">
            <p>{S.iosInstallBody}</p>
            <p className="text-dream-white/70 mt-1">{S.iosInstallSubBody}</p>
          </div>
        </div>
      )}

      <Button
        type="button"
        onClick={handlePrimaryCta}
        disabled={isSubmitting}
        className="w-full h-12 text-base font-semibold"
      >
        {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
        {isIosNonStandalone ? S.primaryCtaIos : S.primaryCta}
      </Button>

      {!isIosNonStandalone && (
        <button
          type="button"
          onClick={goForward}
          className="mt-4 text-sm text-dream-white/70 hover:text-dream-white underline transition-colors"
        >
          {S.secondaryLink}
        </button>
      )}
    </div>
  );
}
